import { getMerchantOption } from '@/components/cashflow/useMerchantSelect';
import {
    StringOption,
    StringOptions,
} from '@/components/common/FancySelectInput';
import { useLazyQuery } from '@apollo/client';
import { SearchMerchantsDocument } from '@common/graphql/generated';
import { debounce } from '@common/util/debounce';
import { useEffect, useMemo } from 'react';

const useMerchantSearch = () => {
    const [searchMerchants] = useLazyQuery(SearchMerchantsDocument, {});

    const loadOptionsDebounced = useMemo(() => {
        const loadOptions = async (
            name: string,
            callback: (options: StringOptions) => void
        ) => {
            const { data } = await searchMerchants({
                variables: {
                    name,
                },
                // If the user has not typed anything, use the cache (since it always returns the same results)
                fetchPolicy: name === '' ? 'cache-first' : 'network-only',
            });

            if (!data?.searchMerchants) {
                return callback([]);
            }

            const options = data.searchMerchants.map(
                (merchant) => getMerchantOption(merchant, true) as StringOption
            );

            if (name === '') {
                return callback([
                    {
                        label: 'Most common',
                        options,
                    },
                ]);
            } else {
                return callback(options);
            }
        };

        return debounce(loadOptions, 100);
    }, [searchMerchants]);

    useEffect(() => {
        return () => {
            loadOptionsDebounced.cancel();
        };
    }, [loadOptionsDebounced]);

    return { loadOptions: loadOptionsDebounced };
};

export default useMerchantSearch;
